import React, { useState } from "react";
import { useFetchKycDetails } from "../../API/Queries/useFetchKycDetails";
import { useRejectDocuments } from "../../API/Queries/useRejectDocuments";
import Loader from "../../Components/Loader";
import GeneralToast from "../../Components/Toaster";
import "./index.css";

const KycDetails = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [userData, setUserData] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const kycMutation = useFetchKycDetails();
  const rejectMutation = useRejectDocuments();

  const handleSearchQueryChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    setSearchQuery(value);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    if (!/^\d{10}$/.test(searchQuery)) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }
    setUserData(null);
    setKycDocuments([]);
    setSelectedDocuments([]);
    setRejectionReason("");
    setErrorMessage("");
    setLoading(true);

    kycMutation.mutate(
      { input: { contactNumber: searchQuery } },
      {
        onSuccess: async (data) => {
          setLoading(false);
          if (data.status === 200 && data?.data?.realmDetails) {
            setUserData(data?.data?.realmDetails);
            const kyc = data?.data?.kycDetails || [];
            if (kyc.length > 0) {
              const documents = kyc.map((item) => ({
                name: item?.documentName || "Unknown Document",
                documentTypeId: item?.documentTypeId,
              }));
              setKycDocuments(documents);
            } else {
              setKycDocuments([]);
              setErrorMessage("No KYC documents found!");
            }
          } else {
            setUserData(null);
            setErrorMessage(data?.errorMessage || "User not found");
          }
        },
        onError: () => {
          setLoading(false);
          setErrorMessage("Error fetching user details. Please try again.");
        },
      }
    );
  };

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setRejectionReason("");
  };

  const handleDocumentSelection = (document) => {
    setSelectedDocuments((prevSelected) => {
      const isSelected = prevSelected.some(
        (doc) => doc?.documentTypeId === document?.documentTypeId
      );
      return isSelected
        ? prevSelected.filter(
            (doc) => doc?.documentTypeId !== document?.documentTypeId
          )
        : [...prevSelected, document];
    });
  };

  const handleRejectDocuments = () => {
    if (!rejectionReason.trim()) {
      return;
    }
    setIsLoading(true);

    const userId = userData?.systemUserId;
    if (!userId) {
      alert("User ID not found.");
      setIsLoading(false);
      return;
    }

    const rejectionPromises = selectedDocuments.map((doc) =>
      rejectMutation.mutateAsync({
        asgardUserId: userId,
        documentTypeId: doc?.documentTypeId,
        rejectionReason,
      })
    );

    Promise.all(rejectionPromises)
      .then((responses) => {
        const allSuccess = responses.every((res) => res?.success === true);

        if (allSuccess) {
          setKycDocuments((prevDocs) =>
            prevDocs.filter(
              (doc) =>
                !selectedDocuments.some(
                  (sel) => sel?.documentTypeId === doc?.documentTypeId
                )
            )
          );
          setSelectedDocuments([]);
          setRejectionReason("");
          closePopup();
        } else {
          const errorResponse = responses.find((res) => res?.success === false);
          setErrorMessage(
            errorResponse?.errorMessage || "Unexpected error occurred"
          );
          closePopup();
        }
      })
      .catch(() => {
        setErrorMessage("An error occurred while rejecting documents.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      {errorMessage && <GeneralToast type="error" message={errorMessage} />}
      {(loading || rejectMutation.isLoading) && <Loader />}
      <div className="container-kyc">
        <div className={`main-content ${isPopupOpen ? "blur-background" : ""}`}>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by Mobile Number"
              value={searchQuery}
              onChange={handleSearchQueryChange}
              maxLength="10"
            />
            <button onClick={handleSearch}>Search</button>
          </div>

          {userData && (
            <div className="kyc-user-details">
              <div className="basic-details">
                <h2>Basic User Details</h2>
                <div className="detail-row">
                  <label>User ID:</label>
                  <span>{userData?.systemUserId || "N/A"}</span>
                </div>
                <div className="detail-row">
                  <label>Name:</label>
                  <span>{userData?.name || "N/A"}</span>
                </div>
                <div className="detail-row">
                  <label>Mobile No.:</label>
                  <span>{userData?.primaryPhoneNumber || "N/A"}</span>
                </div>
              </div>

              <div className="documents">
                <h2>Documents</h2>
                <p>Select the documents which you want to reject</p>
                <div className="document-list">
                  {kycDocuments.length > 0 ? (
                    kycDocuments.map((doc, index) => (
                      <div className="document" key={index}>
                        <input
                          className="select-box"
                          type="checkbox"
                          id={`doc-${index}`}
                          onChange={() => handleDocumentSelection(doc)}
                          checked={selectedDocuments.some(
                            (d) => d?.documentTypeId === doc?.documentTypeId
                          )}
                        />
                        <label htmlFor={`doc-${index}`}>{doc.name}</label>
                      </div>
                    ))
                  ) : (
                    <p>No documents found.</p>
                  )}
                </div>
                <div className="reject-container">
                  <button
                    className={`reject-btn ${
                      selectedDocuments.length > 0 ? "enabled" : "disabled"
                    }`}
                    onClick={() => openPopup()}
                    disabled={selectedDocuments.length === 0}
                  >
                    Reject
                  </button>
                  {selectedDocuments.length > 0 ? (
                    <span className="selected-documents-text">
                      {selectedDocuments.length} Document
                      {selectedDocuments.length > 1 ? "s" : ""} selected
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
        {isPopupOpen && (
          <div className="popup-container">
            {isLoading && <Loader />}
            <div className="popup-header">
              <span>Are you sure about the changes?</span>
              <span className="close-icon" onClick={closePopup}>
                &times;
              </span>
            </div>
            <div className="popup-content">
              <div className="reject-documents">
                <p>Rejecting {selectedDocuments.length} document(s):</p>
                <ul className="list-documents">
                  {selectedDocuments.map((doc, index) => (
                    <li key={index}>{doc.name}</li>
                  ))}
                </ul>
              </div>
              <div className="rejection-reason">
                <label>
                  Rejection Reason <span className="required">*</span>
                </label>
                <textarea
                  placeholder="Enter reason for rejection"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                ></textarea>
              </div>
            </div>
            <div className="popup-footer">
              <button className="no-btn" onClick={closePopup}>
                No
              </button>
              <button
                className={`yes-btn ${rejectionReason.trim() ? "active" : ""}`}
                onClick={handleRejectDocuments}
                disabled={loading}
              >
                {loading ? "Processing..." : "Yes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default KycDetails;
